export type HearingLossThresholds = {
  floor: number;
  normal_max: number;
  mild_max: number;
  moderate_max: number;
  moderately_severe_max: number;
  severe_max: number;
  ceiling: number;
};

export type HearingLossThresholdKey = keyof HearingLossThresholds;

export type HearingLossRange = {
  min: number;
  max: number;
  color: string;
  label: string;
};

export const DEFAULT_HEARING_LOSS_THRESHOLDS: HearingLossThresholds = {
  floor: -10,
  normal_max: 25,
  mild_max: 40,
  moderate_max: 55,
  moderately_severe_max: 70,
  severe_max: 90,
  ceiling: 120
};

export const HEARING_LOSS_THRESHOLD_ORDER: HearingLossThresholdKey[] = [
  "floor",
  "normal_max",
  "mild_max",
  "moderate_max",
  "moderately_severe_max",
  "severe_max",
  "ceiling"
];

export const HEARING_LOSS_THRESHOLD_FIELDS: Array<{
  key: Exclude<HearingLossThresholdKey, "ceiling">;
  label: string;
}> = [
  { key: "floor", label: "Normal lower bound" },
  { key: "normal_max", label: "Normal upper cutoff" },
  { key: "mild_max", label: "Mild upper cutoff" },
  { key: "moderate_max", label: "Moderate upper cutoff" },
  { key: "moderately_severe_max", label: "Moderately Severe upper cutoff" },
  { key: "severe_max", label: "Severe upper cutoff" }
];

const rangeColors = {
  normal: "rgba(34, 139, 34, 0.2)",
  mild: "rgba(144, 195, 0, 0.2)",
  moderate: "rgba(255, 165, 0, 0.2)",
  moderatelySevere: "rgba(255, 120, 0, 0.2)",
  severe: "rgba(255, 69, 0, 0.2)",
  profound: "rgba(178, 34, 34, 0.2)"
};

const toFiniteNumber = (value: unknown, fallback: number) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

export const normalizeHearingLossThresholds = (
  thresholds?: Partial<HearingLossThresholds> | null
): HearingLossThresholds => ({
  floor: toFiniteNumber(thresholds?.floor, DEFAULT_HEARING_LOSS_THRESHOLDS.floor),
  normal_max: toFiniteNumber(
    thresholds?.normal_max,
    DEFAULT_HEARING_LOSS_THRESHOLDS.normal_max
  ),
  mild_max: toFiniteNumber(
    thresholds?.mild_max,
    DEFAULT_HEARING_LOSS_THRESHOLDS.mild_max
  ),
  moderate_max: toFiniteNumber(
    thresholds?.moderate_max,
    DEFAULT_HEARING_LOSS_THRESHOLDS.moderate_max
  ),
  moderately_severe_max: toFiniteNumber(
    thresholds?.moderately_severe_max,
    DEFAULT_HEARING_LOSS_THRESHOLDS.moderately_severe_max
  ),
  severe_max: toFiniteNumber(
    thresholds?.severe_max,
    DEFAULT_HEARING_LOSS_THRESHOLDS.severe_max
  ),
  ceiling: toFiniteNumber(
    thresholds?.ceiling,
    DEFAULT_HEARING_LOSS_THRESHOLDS.ceiling
  )
});

export const validateHearingLossThresholds = (
  thresholds?: Partial<Record<HearingLossThresholdKey, unknown>> | null
): true | string => {
  const values = HEARING_LOSS_THRESHOLD_ORDER.map((key) => {
    const value = thresholds?.[key];
    return value === null || value === undefined || value === ""
      ? Number.NaN
      : Number(value);
  });

  if (values.some((value) => !Number.isFinite(value))) {
    return "All hearing-loss thresholds are required";
  }

  if (values[0] < -20 || values[values.length - 1] > 130) {
    return "Hearing-loss thresholds must stay within -20 to 130 dB HL";
  }

  for (let i = 1; i < values.length; i += 1) {
    if (values[i] <= values[i - 1]) {
      return "Each hearing-loss threshold must be greater than the previous one";
    }
  }

  return true;
};

export const buildHearingLossRanges = (
  thresholds?: Partial<HearingLossThresholds> | null
): HearingLossRange[] => {
  const normalized = normalizeHearingLossThresholds(thresholds);

  return [
    {
      min: normalized.floor,
      max: normalized.normal_max,
      color: rangeColors.normal,
      label: "Normal"
    },
    {
      min: normalized.normal_max,
      max: normalized.mild_max,
      color: rangeColors.mild,
      label: "Mild"
    },
    {
      min: normalized.mild_max,
      max: normalized.moderate_max,
      color: rangeColors.moderate,
      label: "Moderate"
    },
    {
      min: normalized.moderate_max,
      max: normalized.moderately_severe_max,
      color: rangeColors.moderatelySevere,
      label: "Moderately Severe"
    },
    {
      min: normalized.moderately_severe_max,
      max: normalized.severe_max,
      color: rangeColors.severe,
      label: "Severe"
    },
    {
      min: normalized.severe_max,
      max: normalized.ceiling,
      color: rangeColors.profound,
      label: "Profound"
    }
  ];
};
